// Ops sidebar model (9 Oct 2026, ported from Reputifly Build's 8 Oct remodel):
// pure functions for ordering, grouping, search and each row's status. No React,
// no store, no network — tested directly by test/sidebar-model.test.mjs.
//
// Ops semantics on top of Build's: a Clients | Samples switch instead of
// Projects, an Attention section (pending revisions — archived clients too),
// Pinned for the chats the team follows up daily, then Unread, then day groups
// sorted by last activity (automated activity included, so it bumps a chat up).
import type { Company, SessionStatus } from "./types";

/** GET /api/companies/activity, per chat. */
export interface ChatActivity {
  at: number | null;
  role: "user" | "assistant" | null;
  text: string | null;
  archivedAt: string | null;
  pinnedAt: string | null;
}

export type SidebarTab = "clients" | "samples";
/** The header's Filter button: everything, only unread chats, or only pinned ones (remembered per device). */
/** The header's Filter. Working (Julian's UI round 2): every chat with an agent running right now. */
export type SidebarFilter = "all" | "unread" | "working" | "pinned";
export const FILTER_ORDER: readonly SidebarFilter[] = ["all", "unread", "working", "pinned"];
export const FILTER_LABEL: Record<SidebarFilter, string> = { all: "All chats", unread: "Unread", working: "Working", pinned: "Pinned" };
export function isSidebarFilter(v: unknown): v is SidebarFilter { return v === "all" || v === "unread" || v === "working" || v === "pinned"; }
/** An agent is running in this chat right now: starting, or running a turn (yours or an automated one). */
export function isWorking(status?: SessionStatus | null): boolean {
  return status?.state === "spawning" || (status?.state === "running" && (status.busy === true || !!status.autoTurn));
}
export type DayBucket = "today" | "yesterday" | "week" | "older";
export const BUCKET_LABEL: Record<DayBucket, string> = {
  today: "Today", yesterday: "Yesterday", week: "Previous 7 days", older: "Older",
};
const BUCKET_ORDER: DayBucket[] = ["today", "yesterday", "week", "older"];
const DAY = 86_400_000;
const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parse(iso: string | undefined | null): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
}

/* ---------------------------------------------------------------- kinds */

/**
 * What a row is, in Julian's and Farhan's words:
 *  - "client": a real client (also every entry with no kind — the stricter delete applies);
 *  - "sample": a demo site for a lead;
 *  - "chat":   a sample chat whose website does not exist yet ("Chat (no site yet)").
 */
export type ChatKind = "client" | "sample" | "chat";
export const KIND_LABEL: Record<ChatKind, string> = { client: "Client", sample: "Sample", chat: "Chat (no site yet)" };

export function chatKind(chat: Company): ChatKind {
  if (chat.kind !== "sample") return "client";
  return chat.apiBase && chat.apiBase.trim() ? "sample" : "chat";
}

export function inTab(chat: Company, tab: SidebarTab): boolean {
  return tab === "samples" ? chat.kind === "sample" : chat.kind !== "sample";
}

/** The site host for a row's second line and for search ("acme.reputifly.cloud"). */
export function siteHost(chat: Company): string {
  if (!chat.apiBase) return "";
  try { return new URL(chat.apiBase).host; } catch { return ""; }
}

/* ---------------------------------------------------------------- flags + time */

/** When this chat last moved: a message of any kind, an Exchange or revision change, an unread reply. */
export function lastActivityMs(chat: Company, activity?: ChatActivity | null, unreadAt?: number | null): number {
  return Math.max(parse(chat.lastActiveAt), parse(chat.createdAt), activity?.at ?? 0, unreadAt ?? 0);
}

export function isArchived(chat: Company): boolean {
  return typeof chat.archivedAt === "string" && chat.archivedAt.length > 0;
}

/** An archived chat is never on top, whatever a stale flag says. */
export function isPinned(chat: Company): boolean {
  return typeof chat.pinnedAt === "string" && chat.pinnedAt.length > 0 && !isArchived(chat);
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Calendar days in local time, so "Yesterday" means the day before today, not 24 h ago. */
export function dayBucket(ms: number, now = Date.now()): DayBucket {
  const today = startOfDay(now);
  if (ms >= today) return "today";
  if (ms >= today - DAY) return "yesterday";
  if (ms >= today - 7 * DAY) return "week";
  return "older";
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "17:33" today, "Yesterday", "Mon" this week, "3 Oct" this year, "3 Oct 2025" before. */
export function formatRowTime(ms: number, now = Date.now()): string {
  if (!ms) return "";
  const d = new Date(ms);
  const bucket = dayBucket(ms, now);
  if (bucket === "today") return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (bucket === "yesterday") return "Yesterday";
  if (bucket === "week") return WEEKDAY[d.getDay()];
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return `${d.getDate()} ${MONTH[d.getMonth()]}${sameYear ? "" : ` ${d.getFullYear()}`}`;
}

/** The exact moment: "Wed 8 Oct 2026, 17:33" (`year: "auto"` drops this year's year). */
export function formatExactTime(ms: number, now = Date.now(), year: "always" | "auto" = "always"): string {
  if (!ms) return "";
  const d = new Date(ms);
  const showYear = year === "always" || d.getFullYear() !== new Date(now).getFullYear();
  return `${WEEKDAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]}${showYear ? ` ${d.getFullYear()}` : ""}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ---------------------------------------------------------------- search */

/** Lower case, accents dropped, punctuation runs read as one space; `map[i]` = index in the original. */
export function foldText(text: string): { folded: string; map: number[] } {
  let folded = "";
  const map: number[] = [];
  let lastSpace = true;
  for (let i = 0; i < text.length; i++) {
    const base = text[i].normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();
    for (const ch of base) {
      if (/[\p{L}\p{N}]/u.test(ch)) { folded += ch; map.push(i); lastSpace = false; }
      else if (!lastSpace) { folded += " "; map.push(i); lastSpace = true; }
    }
  }
  if (folded.endsWith(" ")) { folded = folded.slice(0, -1); map.pop(); }
  return { folded, map };
}

export function queryWords(query: string): string[] {
  const words = foldText(query).folded.split(" ").filter(Boolean);
  return Array.from(new Set(words));
}

function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    const diff: number[] = [];
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) { diff.push(i); if (diff.length > 2) return false; }
    if (diff.length === 1) return true;
    return diff.length === 2 && diff[1] === diff[0] + 1 && a[diff[0]] === b[diff[1]] && a[diff[1]] === b[diff[0]];
  }
  const [s, l] = a.length < b.length ? [a, b] : [b, a];
  let i = 0, j = 0, skipped = false;
  while (i < s.length && j < l.length) {
    if (s[i] === l[j]) { i++; j++; continue; }
    if (skipped) return false;
    skipped = true; j++;
  }
  return true;
}

/** 4 whole word, 3 word start, 2 inside a word (3+ letters), 1 one typo away (4+ letters), 0 none. */
export function wordMatch(word: string, folded: string): number {
  if (!word || !folded) return 0;
  let best = 0;
  let index = folded.indexOf(word);
  while (index >= 0 && best < 4) {
    const atStart = index === 0 || folded[index - 1] === " ";
    const end = index + word.length;
    const atEnd = end === folded.length || folded[end] === " ";
    if (atStart && atEnd) best = 4;
    else if (atStart) best = Math.max(best, 3);
    else if (word.length >= 3) best = Math.max(best, 2);
    index = folded.indexOf(word, index + 1);
  }
  if (best === 0 && word.length >= 4) {
    for (const part of folded.split(" ")) {
      if (withinOneEdit(word, part) || (part.length > word.length && withinOneEdit(word, part.slice(0, word.length)))) return 1;
    }
  }
  if (best === 0 && word.length >= 4 && folded.replace(/ /g, "").includes(word)) return 2;
  return best;
}

const FIELD_WEIGHT = { name: 100, site: 10, preview: 1 } as const;
export type SearchField = keyof typeof FIELD_WEIGHT;

/** Every query word must match a field; name beats site beats preview. */
export function searchScore(words: string[], fields: Record<SearchField, string>): number {
  if (words.length === 0) return 1;
  const folded = {
    name: foldText(fields.name).folded,
    site: foldText(fields.site).folded,
    preview: foldText(fields.preview).folded,
  };
  let score = 0;
  for (const word of words) {
    let best = 0;
    for (const key of Object.keys(FIELD_WEIGHT) as SearchField[]) {
      const m = wordMatch(word, folded[key]);
      if (m) best = Math.max(best, FIELD_WEIGHT[key] * m);
    }
    if (best === 0) return 0;
    score += best;
  }
  const phrase = words.join(" ");
  if (words.length > 1 && folded.name.includes(phrase)) score += 50;
  if (folded.name === phrase) score += 100;
  return score;
}

/** Ranges [start, end) to highlight for a search. */
export function matchRanges(text: string, query: string): Array<[number, number]> {
  const words = queryWords(query);
  if (!text || words.length === 0) return [];
  const { folded, map } = foldText(text);
  const ranges: Array<[number, number]> = [];
  for (const word of words) {
    let index = folded.indexOf(word);
    while (index >= 0) {
      const atStart = index === 0 || folded[index - 1] === " ";
      if (atStart || word.length >= 3) ranges.push([map[index], map[index + word.length - 1] + 1]);
      index = folded.indexOf(word, index + word.length);
    }
  }
  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  return merged;
}

/* ---------------------------------------------------------------- row state */

export type RowTone = "needs" | "working" | "starting" | "unread" | "none";
export interface RowState { tone: RowTone; label?: string; a11y?: string }

/** Amber (a helper needs you) outranks working, working outranks blue (unread). */
export function rowState(input: { status?: SessionStatus; unread: boolean; isActive: boolean }): RowState {
  const { status } = input;
  const helpersNeed = status?.state === "running" ? status.helpers?.needsYou ?? 0 : 0;
  if (helpersNeed > 0) {
    return { tone: "needs", label: `Needs you · ${helpersNeed === 1 ? "1 helper is" : `${helpersNeed} helpers are`} waiting`, a11y: "Needs you" };
  }
  if (status?.state === "spawning") return { tone: "starting", label: "Starting…", a11y: "Starting" };
  if (status?.state === "running" && (status.busy === true || !!status.autoTurn)) return { tone: "working", label: "Working…", a11y: "Working" };
  if (input.unread && !input.isActive) return { tone: "unread", a11y: "New reply" };
  return { tone: "none" };
}

/* ---------------------------------------------------------------- sections */

export interface SidebarInputs {
  chats: Company[];
  tab: SidebarTab;
  activity: Record<string, ChatActivity | undefined>;
  unread: Record<string, number | undefined>;
  /** companyId → pending revision count (Attention). */
  attention: Record<string, number | undefined>;
  query: string;
  archived: boolean;
  /** The open chat stays in the main list even when archived (marked "Archived"). */
  activeId?: string | null;
  /** Chats archived / unarchived from this list just now: they leave their list at once. */
  movedIds?: ReadonlySet<string>;
  /** Filter button (default "all"). Ignored while searching and in the Archived view. */
  filter?: SidebarFilter;
  /** Chats with an agent running right now (isWorking), for the Working filter. */
  working?: ReadonlySet<string>;
}

const filtering = (input: SidebarInputs) => !!input.filter && input.filter !== "all" && !input.archived && !searching(input);

const searching = (input: SidebarInputs) => queryWords(input.query).length > 0;

function chatScore(chat: Company, words: string[], input: SidebarInputs): number {
  return searchScore(words, { name: chat.name, site: siteHost(chat), preview: input.activity[chat.id]?.text ?? "" });
}

const atOf = (input: SidebarInputs) => (c: Company) => lastActivityMs(c, input.activity[c.id], input.unread[c.id]);

/**
 * Attention: clients with pending revisions (Clients tab, not searching, not the
 * Archived view). Archived clients are included on purpose — revisions are never hidden.
 */
export function attentionChats(input: SidebarInputs): Company[] {
  if (input.archived || input.tab !== "clients" || searching(input)) return [];
  const at = atOf(input);
  return input.chats
    .filter((c) => inTab(c, "clients") && (input.attention[c.id] ?? 0) > 0)
    .sort((a, b) => at(b) - at(a) || a.name.localeCompare(b.name));
}

/** Pinned chats of this tab, in the order they were pinned (a stable list that never reshuffles). */
export function pinnedChats(input: SidebarInputs): Company[] {
  // A filtered view is one plain list (the Pinned filter lists them itself, as rows).
  if (input.archived || searching(input) || filtering(input)) return [];
  const inAttention = new Set(attentionChats(input).map((c) => c.id));
  return input.chats
    .filter((c) => inTab(c, input.tab) && isPinned(c) && !inAttention.has(c.id))
    .sort((a, b) => parse(a.pinnedAt) - parse(b.pinnedAt) || a.name.localeCompare(b.name));
}

/**
 * Every other chat this view shows. Without a search: newest activity first. With
 * one: best match first (name over site over preview), newest activity breaking ties.
 */
export function visibleChats(input: SidebarInputs): Company[] {
  const words = queryWords(input.query);
  const lifted = new Set([...attentionChats(input), ...pinnedChats(input)].map((c) => c.id));
  const scores = new Map<string, number>();
  const rows = input.chats.filter((chat) => {
    if (!inTab(chat, input.tab)) return false;
    if (lifted.has(chat.id)) return false;
    const keepOpen = chat.id === input.activeId && !input.archived && !input.movedIds?.has(chat.id);
    if (isArchived(chat) !== input.archived && !keepOpen) return false;
    if (words.length === 0) return true;
    const score = chatScore(chat, words, input);
    scores.set(chat.id, score);
    return score > 0;
  });
  const at = atOf(input);
  return rows.sort((a, b) => (scores.get(b.id) ?? 0) - (scores.get(a.id) ?? 0) || at(b) - at(a) || a.name.localeCompare(b.name));
}

export interface ListSection { key: DayBucket | "results" | "unread" | "filter"; label: string; chats: Company[] }

/**
 * The Filter button's list. Unread: every unread chat of this tab (pinned ones too), newest
 * first. Working: every chat with an agent running right now (pinned ones too), latest activity
 * first. Pinned: every pinned chat in pin order, as full rows. Attention keeps its own place above.
 */
function filteredSection(input: SidebarInputs): ListSection[] {
  const inAttention = new Set(attentionChats(input).map((c) => c.id));
  let chats: Company[];
  if (input.filter === "working") {
    const at = atOf(input);
    chats = input.chats
      .filter((c) => inTab(c, input.tab) && !inAttention.has(c.id) && !!input.working?.has(c.id)
        && (!isArchived(c) || c.id === input.activeId))
      .sort((a, b) => at(b) - at(a) || a.name.localeCompare(b.name));
  } else if (input.filter === "unread") {
    chats = input.chats
      .filter((c) => inTab(c, input.tab) && !inAttention.has(c.id) && !!input.unread[c.id]
        && (!isArchived(c) || c.id === input.activeId))
      .sort((a, b) => (input.unread[b.id] ?? 0) - (input.unread[a.id] ?? 0) || a.name.localeCompare(b.name));
  } else {
    chats = input.chats
      .filter((c) => inTab(c, input.tab) && isPinned(c) && !inAttention.has(c.id))
      .sort((a, b) => parse(a.pinnedAt) - parse(b.pinnedAt) || a.name.localeCompare(b.name));
  }
  return chats.length ? [{ key: "filter", label: FILTER_LABEL[input.filter!], chats }] : [];
}

/**
 * Unread (a finished reply nobody opened yet), then day groups; a search is one
 * ranked list ("Best matches"). Pinned and Attention are separate (see above).
 */
export function listSections(input: SidebarInputs, now = Date.now()): ListSection[] {
  if (filtering(input)) return filteredSection(input);
  const chats = visibleChats(input);
  if (searching(input)) return chats.length ? [{ key: "results", label: "Best matches", chats }] : [];
  if (input.archived) return chats.length ? [{ key: "results", label: "", chats }] : [];
  const unread = chats.filter((c) => input.unread[c.id] && c.id !== input.activeId)
    .sort((a, b) => (input.unread[b.id] ?? 0) - (input.unread[a.id] ?? 0));
  const unreadIds = new Set(unread.map((c) => c.id));
  const buckets = new Map<DayBucket, Company[]>();
  const at = atOf(input);
  for (const chat of chats) {
    if (unreadIds.has(chat.id)) continue;
    const key = dayBucket(at(chat), now);
    (buckets.get(key) ?? buckets.set(key, []).get(key)!).push(chat);
  }
  return [
    ...(unread.length ? [{ key: "unread" as const, label: "Unread", chats: unread }] : []),
    ...BUCKET_ORDER.filter((k) => buckets.has(k)).map((key) => ({ key, label: BUCKET_LABEL[key], chats: buckets.get(key)! })),
  ];
}

export function archivedCount(chats: Company[], tab: SidebarTab): number {
  return chats.filter((c) => inTab(c, tab) && isArchived(c)).length;
}

/** Preview line: "You: …" for the operator's own last message, the reply text otherwise. */
export function previewText(activity?: ChatActivity | null): string {
  if (!activity?.text) return "";
  return activity.role === "user" ? `You: ${activity.text}` : activity.text;
}

/** Second line when there is no preview: a site-less sample says when it was made. */
export function quietLine(chat: Company, now = Date.now()): string {
  const kind = chatKind(chat);
  if (kind === "chat") {
    const made = parse(chat.createdAt);
    // The row already carries a "No site yet" tag: this line just says when it started.
    return made ? `Started ${formatRowTime(made, now)}` : "Waiting for its first message";
  }
  return "No messages yet";
}

/* ---------------------------------------------------------------- delete wording */

export interface DeleteCopy { title: string; body: string; confirm: string; requireName: boolean; archiveFirst: boolean }

/** The delete sheet's words per kind (plan C1b). Clients need the name typed and offer Archive first. */
export function deleteCopy(chat: Company): DeleteCopy {
  const kind = chatKind(chat);
  if (kind === "chat") return { title: `Delete “${chat.name}”?`, body: "Delete this chat — no website exists for it.", confirm: "Delete chat", requireName: false, archiveFirst: false };
  if (kind === "sample") return { title: `Delete “${chat.name}”?`, body: "Delete this sample chat — the demo site is not touched.", confirm: "Delete sample", requireName: false, archiveFirst: false };
  return {
    title: `Remove “${chat.name}”?`,
    body: `Remove ${chat.name} from Operations: its saved site key, revision automation and chat history are deleted. The website itself is not touched. To tidy the list, Archive instead.`,
    confirm: "Remove client", requireName: true, archiveFirst: !isArchived(chat),
  };
}

/** The typed-name check for a client delete: same name, ignoring case and outer spaces. */
export function nameConfirmed(chat: Company, typed: string): boolean {
  return typed.trim().toLowerCase() === chat.name.trim().toLowerCase() && typed.trim().length > 0;
}

/* ---------------------------------------------------------------- pinned at scale (9 Oct, no inner scroll) */

/**
 * The words a working chat cycles through (sidebar rows and pinned items, ~2.5 s each, the
 * SwapText slide/blur). Reduced motion keeps the first. Plain verbs, nothing that claims more.
 */
export const WORKING_VERBS = ["Working…", "Thinking…", "Reading…", "Writing…"] as const;
export const WORKING_VERB_MS = 2500;

/** A pinned face tile's label: "Zenith Living - Ian Lee - 9/10" → "Zenith Living" (full name in the tooltip / label / sheet). */
export function shortName(name: string): string {
  const head = String(name ?? "").split(/\s+[-–—|]\s+/)[0]?.trim();
  return head || String(name ?? "").trim();
}

export interface PinnedFold<T> { shown: T[]; hidden: T[]; folded: boolean }

/**
 * Pinned never gets its own scroll box (Julian, 9 Oct: "what do you mean I have to scroll it?").
 * Up to `budget` pins: every one. More: the first budget − 1 IN PIN ORDER plus one "Show N more"
 * line / tile (so N ≥ 2, never "Show 1 more"). Membership never follows activity; a reply or a
 * starting agent never moves a pin in or out (julian-ui: never re-sort by activity). The only
 * exceptions are things the person just did: `keep` (in priority order: the chat being renamed,
 * the chat just pinned, the open chat) takes the last shown slots when it would be hidden.
 * Whatever is shown is drawn in pin order. `expanded` (remembered per device) shows every pin.
 */
export function foldPinned<T extends { id: string }>(pins: T[], budget: number, keep: ReadonlyArray<string | null | undefined> = [], expanded = false): PinnedFold<T> {
  const cap = Math.max(2, Math.floor(Number.isFinite(budget) ? budget : pins.length));
  if (expanded || pins.length <= cap) return { shown: pins.slice(), hidden: [], folded: false };
  const slots = cap - 1;
  const ids = new Set(pins.map((p) => p.id));
  const want = Array.from(new Set(keep.filter((id): id is string => !!id && ids.has(id)))).slice(0, slots);
  const wanted = new Set(want);
  const chosen = new Set(pins.slice(0, slots).map((p) => p.id));
  for (const id of want) {
    if (chosen.has(id)) continue;
    const victim = [...pins].reverse().find((p) => chosen.has(p.id) && !wanted.has(p.id));
    if (!victim) break;
    chosen.delete(victim.id);
    chosen.add(id);
  }
  return { shown: pins.filter((p) => chosen.has(p.id)), hidden: pins.filter((p) => !chosen.has(p.id)), folded: true };
}

/**
 * The room the sidebar list really has, measured on the page (fixer r1 #1). `list` is the list's
 * visible height with the Undo bar's room counted in (an Undo never folds a pin); the sizes are
 * the CSS ones in use (mouse / touch / phone): a full chat row, a compact pinned row, the Pinned
 * head, a section label and a phone face tile.
 */
export interface PinRoom { list: number; attentionRows: number; rowH: number; pinH: number; headH: number; labelH: number; tileH: number }
/** Rows in a section sit 2 px apart; Attention → Pinned is 10 px; Pinned's bottom padding + rule + the 4 px under it = 13. */
const ROW_GAP = 2, ATTENTION_AFTER = 10, PINNED_AFTER = 13;
export const PIN_LINES_MIN = 3, PIN_LINES_MAX = 8;

/**
 * How many lines (laptop / touch panel rows) or cells (phone tiles) Pinned may use before it folds.
 * With the measured room: whatever fits under Attention while the next section's label and one full
 * chat row stay in view (Julian: pins must never push today's chats off the screen), 3 to 8 lines;
 * the phone grid keeps its second row of faces only if that row fits the same way. Without a
 * measurement (first paint): by window height. The caller re-measures on resize, never on a status
 * poll, and passes a FIXED Attention room (2 rows), never the live count: the fold must not change
 * when Attention arrives after load or a revision lands (break-it r2), so it holds still.
 */
export function pinBudget(viewportHeight: number, viewportWidth: number, room?: PinRoom | null): { lines: number; gridRows: number; gridCols: number } {
  const gridCols = viewportWidth < 360 ? 4 : 5;
  if (!room || !(room.list > 0)) {
    return { lines: viewportHeight >= 860 ? 8 : viewportHeight >= 600 ? 6 : 4, gridRows: viewportHeight >= 640 ? 2 : 1, gridCols };
  }
  const attention = room.attentionRows > 0 ? room.labelH + room.attentionRows * (room.rowH + ROW_GAP) - ROW_GAP + ATTENTION_AFTER : 0;
  const free = room.list - attention - room.headH - PINNED_AFTER - room.labelH - room.rowH;
  const lines = Math.max(PIN_LINES_MIN, Math.min(PIN_LINES_MAX, Math.floor((free + ROW_GAP) / (room.pinH + ROW_GAP))));
  // Two rows of tiles = 2 tiles + the 2 px gap + the grid's 2 px top margin.
  const gridRows = free - (2 * room.tileH + 4) >= 0 ? 2 : 1;
  return { lines, gridRows, gridCols };
}

/**
 * The Filter button's counts, from the same lists the views show, so a number never promises
 * a row that is not there. A pinned chat with a revision waiting lives in Attention (with a pin
 * mark), so it is not in the Pinned count, the Pinned head or the Pinned filter's list.
 */
/* ------------------------------------------------------- laptop panel width */

/** The chats panel on a laptop (≥ 1100 px with a mouse, where it pushes the chat): drag its right
 *  edge (Build's, 9 Oct round 2). 300 px by default (Build's; 280 cut most real chat names), 260–520,
 *  and always leaving the chat 560 px. Remembered per device. */
export const SIDEBAR_WIDTH = { default: 300, min: 260, max: 520, chatMin: 560, step: 16, bigStep: 64 } as const;
export const SIDEBAR_WIDTH_KEY = "ops.sidebar.width.v1";

/** The width the panel can have in a window this wide (whole pixels). */
export function clampSidebarWidth(width: number, viewport: number): number {
  const room = Number.isFinite(viewport) ? viewport - SIDEBAR_WIDTH.chatMin : Infinity;
  const max = Math.max(SIDEBAR_WIDTH.min, Math.min(SIDEBAR_WIDTH.max, room));
  const value = Number.isFinite(width) ? width : SIDEBAR_WIDTH.default;
  return Math.round(Math.max(SIDEBAR_WIDTH.min, Math.min(max, value)));
}

/** The width chosen on this device, or null for the default. */
export function readSidebarWidth(storage?: Pick<Storage, "getItem"> | null): number | null {
  try {
    const raw = storage?.getItem(SIDEBAR_WIDTH_KEY);
    const value = raw == null ? NaN : Number(raw);
    return Number.isFinite(value) && value >= SIDEBAR_WIDTH.min && value <= SIDEBAR_WIDTH.max ? Math.round(value) : null;
  } catch {
    return null;
  }
}

/** null (or the default) forgets the choice: the panel goes back to 300 px. */
export function writeSidebarWidth(width: number | null, storage?: Pick<Storage, "setItem" | "removeItem"> | null): void {
  try {
    if (width == null || width === SIDEBAR_WIDTH.default) storage?.removeItem(SIDEBAR_WIDTH_KEY);
    else storage?.setItem(SIDEBAR_WIDTH_KEY, String(Math.round(width)));
  } catch { /* private mode: just not remembered */ }
}

export function filterCounts(input: SidebarInputs, now = Date.now()): Record<SidebarFilter, number> {
  const base: SidebarInputs = { ...input, query: "", archived: false };
  const listed = (filter: SidebarFilter) => listSections({ ...base, filter }, now)[0]?.chats.length ?? 0;
  // All chats = every row the All view shows: Attention (an archived client with a revision too),
  // Pinned and the dated list (the open chat kept there after it was archived too), each once.
  const allView: SidebarInputs = { ...base, filter: "all" };
  return {
    all: new Set([...attentionChats(allView), ...pinnedChats(allView), ...visibleChats(allView)].map((c) => c.id)).size,
    unread: listed("unread"),
    working: listed("working"),
    pinned: pinnedChats({ ...base, filter: "all" }).length,
  };
}

export interface PopoverPlacement { left: number; top: number; maxHeight?: number; side: "below" | "above" | "right" | "left" | "clamped" }

/**
 * Where an anchored popover goes (the ⋯ menu, the Filter popover). Below its button if the
 * measured panel fits, else above, else beside it (right, then left, centred on it and slid to
 * fit), and only if none of those fit, clamped with a max height. Always `margin` inside the
 * window; below / above / beside never cover the button. `align` lines the panel up with the
 * button's end edge (⋯), start edge (a right-click point) or centre (Filter).
 */
export function placePopover(
  anchor: { left: number; top: number; right: number; bottom: number },
  size: { width: number; height: number },
  view: { width: number; height: number },
  opts: { align?: "end" | "start" | "center"; gap?: number; margin?: number } = {},
): PopoverPlacement {
  const gap = opts.gap ?? 6, m = opts.margin ?? 8, align = opts.align ?? "end";
  const w = Math.min(size.width, view.width - 2 * m), h = size.height;
  const clampX = (x: number) => Math.max(m, Math.min(x, view.width - w - m));
  const clampY = (y: number) => Math.max(m, Math.min(y, view.height - h - m));
  const x0 = align === "end" ? anchor.right - w : align === "start" ? anchor.left : (anchor.left + anchor.right) / 2 - w / 2;
  const left = clampX(x0);
  if (anchor.bottom + gap + h <= view.height - m) return { left, top: anchor.bottom + gap, side: "below" };
  if (anchor.top - gap - h >= m) return { left, top: anchor.top - gap - h, side: "above" };
  const midY = (anchor.top + anchor.bottom) / 2 - h / 2;
  if (h <= view.height - 2 * m) {
    if (anchor.right + gap + w <= view.width - m) return { left: anchor.right + gap, top: clampY(midY), side: "right" };
    if (anchor.left - gap - w >= m) return { left: anchor.left - gap - w, top: clampY(midY), side: "left" };
  }
  return { left, top: m, maxHeight: Math.max(120, view.height - 2 * m), side: "clamped" };
}
